import { Log } from '../../../AuxillaryService/Log';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import AdQueue from '../AdQueue';
import { VengoAd, VengoBaseUrls } from './types';
import {
    UnformattedVastAdData,
    VastResponseBodyTag,
    vastResponseBodyDataFormatter,
} from '../types';
import { ZodError } from 'zod';
import { NoNewContentError } from '../errors';

export class VengoApiHelper {
    /**
     * fetches ads from the Vengo API
     * & enqueues fetched ads to the Vengo Ad
     * queue.
     */

    static async fetchAds(
        vengoOrganizationId: string,
        vengoPlayerId: string,
        adQueue: AdQueue<VengoAd>,
        numberOfAds: number,
        vengoApiBaseUrl: VengoBaseUrls
    ): Promise<void> {
        Log.Info(
            `Vengo: fetching ads ${numberOfAds} for player id  ${vengoPlayerId}`,
            null,
            __fileline
        );

        const config: AxiosRequestConfig = {
            method: 'GET',
            url: `${vengoApiBaseUrl}/trafficking/vast/ad_request`,
            params: {
                organization_id: vengoOrganizationId,
                ad_unit_id: vengoPlayerId,
            },
            timeout: 6000,
        };

        for (let i = 0; i <= numberOfAds; i++) {
            const now: Date = new Date();

            const fifteenMinutesFromNow = new Date(now.getTime() + 15 * 60 * 1000);
            const unixSecondsTimestamp: number = Math.floor(fifteenMinutesFromNow.getTime() / 1000);

            let response: AxiosResponse;

            try {
                response = await axios.request(config);
            } catch (error) {
                const errorMessage = this.getErrorMessage(error);

                Log.Error(
                    `Vengo: Failed to fetch ads for player id ${vengoPlayerId} organization id ${vengoOrganizationId} ${errorMessage}`
                );
                return;
            }

            let unformattedVastData: UnformattedVastAdData;

            try {
                unformattedVastData = this.getUnformattedAdDataFromXml(response);
            } catch (error: unknown) {
                const errorMessage = this.getErrorMessage(error);

                if (error instanceof NoNewContentError) {
                    Log.Info(
                        `${this.name} No content scheduled for player id ${vengoPlayerId} organization id ${vengoOrganizationId}`,
                        null,
                        __fileline
                    );
                    return;
                }
                Log.Error(
                    `Vengo: Failed to fetch ads for player id ${vengoPlayerId} Organization id ${vengoOrganizationId} Failed to query xml doc for vast data ${errorMessage}`,
                    null,
                    __fileline
                );
                return;
            }

            const parseResult = vastResponseBodyDataFormatter.safeParse({
                id: unformattedVastData.adId,
                popUrl: unformattedVastData.popUrl,
                duration: unformattedVastData.duration,
                filetype: unformattedVastData.filetype,
                assetUrl: unformattedVastData.mediaFileTextContent,
            });

            if (!parseResult.success) {
                const anError: ZodError = parseResult.error;
                Log.Error(
                    `Vengo: Failed to fetch ads for player id ${vengoPlayerId} Organization id ${vengoOrganizationId}; Failed to format vast data: ${anError}`,
                    null,
                    __fileline
                );
                return;
            }
            const adResponseData: VastResponseBodyTag = parseResult.data;

            const anAd: VengoAd = {
                adId: adResponseData.id,
                assetUrl: adResponseData.assetUrl,
                duration: adResponseData.duration,
                isFallback: false,
                filetype: adResponseData.filetype,
                playcount: 0,
                lastAdInLoop: false,
                popUrl: adResponseData.popUrl,
                expires: unixSecondsTimestamp,
            };

            adQueue.enqueue(anAd);
        }
    }

    static async sendProofOfPlay(
        ad: VengoAd,
        vengoPlayerId: string,
        vengoOrganizationId: string
    ): Promise<void> {
        const config: AxiosRequestConfig = {
            method: 'GET',
            url: ad.popUrl,
            headers: {
                'Content-Type': 'application/json',
            },
        };

        try {
            await axios.request(config);
        } catch (error: unknown) {
            const errorMessage = this.getErrorMessage(error);

            Log.Error(
                `Vengo: Failed to send proof of play for ad ${ad.adId} for player ${vengoPlayerId} organization id ${vengoOrganizationId} : ${errorMessage}`,
                null,
                __fileline
            );
            return;
        }

        Log.Info(
            `Vengo: Successfully sent proof of play for ad ${ad.adId} for player ${vengoPlayerId} organization id ${vengoOrganizationId}`,
            null,
            __fileline
        );
    }

    /**
     * Takes a thrown value of type
     * unknown and returns the `message` prop
     * if it is an instance of an Error, or returns
     * the value of it wrapped in a String constructor.
     */
    static getErrorMessage(error: unknown): string {
        if (error instanceof Error) {
            return error.message;
        }
        return String(error);
    }

    /**
     * Attempts to query the xml response
     * body for VAST tags required for the ad objects.
     */
    static getUnformattedAdDataFromXml(response: AxiosResponse): UnformattedVastAdData {
        const parser = new DOMParser();

        const xmlDoc = parser.parseFromString(response.data, 'text/xml');

        const vastTag = xmlDoc.getElementsByTagName('VAST')[0];

        if (vastTag.children.length === 0) {
            throw new NoNewContentError();
        }

        const adTag = vastTag.getElementsByTagName('Ad')[0];
        const adId = adTag.getAttribute('id');
        const mediaFilesTag = adTag.getElementsByTagName('MediaFiles')[0];
        const firstMediaFileTag = mediaFilesTag.getElementsByTagName('MediaFile')[0];
        const creatives = adTag.getElementsByTagName('Creatives')[0];
        const duration = creatives.getElementsByTagName('Duration')[0].textContent;
        const mediaFileTextContent = firstMediaFileTag.textContent;
        const filetype = firstMediaFileTag.getAttribute('type');
        const popUrl = adTag.getElementsByTagName('Impression')[0].textContent;

        return { adId, popUrl, duration, filetype, mediaFileTextContent };
    }
}
